import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  DeleteOutline,
  Info,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useAppDispatch } from "../../redux/store";
import {
  deleteRow,
  clearShiftCell,
  setUploadStatus,
} from "../../redux/features/scheduleUpload/scheduleUploadSlice";
import {
  getShiftCategory,
  SHIFT_COLORS,
  ScheduleRow,
} from "../../utils/parseScheduleExcel";
import { StepIndicator } from "../ScheduleUpload";
import { Clear } from "@mui/icons-material";

// ─── Shift Cell ───────────────────────────────────────────────────────────────

const ShiftCell: React.FC<{
  value: string;
  onClear: () => void;
}> = ({ value, onClear }) => {
  const cat = getShiftCategory(value);
  const s = SHIFT_COLORS[cat];
  const isEmpty = cat === "empty";

  return (
    <Tooltip title={s.label} arrow placement="top">
      <Box
        sx={{
          position: "relative",
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          background: s.bg,
          color: s.text,
          fontWeight: isEmpty ? 400 : 700,
          fontSize: 11.5,
          textAlign: "center",
          minWidth: 62,
          maxWidth: 90,
          border: `1px solid ${s.borderColor}`,
          letterSpacing: 0.2,
          userSelect: "none",
          opacity: isEmpty ? 0.45 : 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "&:hover .clear-btn": {
            opacity: 1,
          },
        }}
      >
        {isEmpty ? "—" : value}
        
        {/* Cell Delete Button */}
        {!isEmpty && (
          <Box
            className="clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              background: "rgba(255, 0, 0, 0.1)",
              color: "error.main",
              borderRadius: "50%",
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: 0,
              transition: "opacity 0.2s",
              "&:hover": {
                background: "rgba(255, 0, 0, 0.2)",
              },
            }}
          >
            <Clear sx={{ fontSize: 12 }} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── Legend ───────────────────────────────────────────────────────────────────

const Legend = () => (
  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>
      Legend:
    </Typography>
    {(["morning", "afternoon", "off", "leave", "special"] as const).map((cat) => {
      const s = SHIFT_COLORS[cat];
      return (
        <Box
          key={cat}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.2,
            py: 0.3,
            borderRadius: 1,
            border: `1px solid ${s.borderColor}`,
            background: s.bg,
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: s.text }} />
          <Typography sx={{ fontSize: 11, color: s.text, fontWeight: 600 }}>
            {cat === "morning"
              ? "Morning (before 12:00)"
              : cat === "afternoon"
              ? "Afternoon (12:00+)"
              : cat === "off"
              ? "Off – no shift"
              : cat === "leave"
              ? "Leave – 8 hrs time off"
              : "Special / custom code"}
          </Typography>
        </Box>
      );
    })}
  </Stack>
);

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────

const DeleteDialog: React.FC<{
  employee: string;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ employee, open, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>Remove Employee?</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        Remove <strong>{employee}</strong> from this schedule submission?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} color="inherit">Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained" sx={{ borderRadius: 50 }}>
        Remove
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Role colour map ──────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  "Morning Only":   "#1b5e20",
  "Afternoon Only": "#0d47a1",
  "Night Team":     "#311b92",
  "Receptionist":   "#880e4f",
  "Odds":           "#bf360c",
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ScheduleReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { parsedData } = useSelector((state: RootState) => state.scheduleUpload);
  const user = useSelector((state: RootState) => state.user.user);

  const [activeWeek, setActiveWeek] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleRow | null>(null);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!parsedData || parsedData.rows.length === 0) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 10, textAlign: "center", px: 3 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No parsed schedule found. Please go back and upload an Excel file first.
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/schedule-upload")} sx={{ mt: 2 }}>
          Back to Upload
        </Button>
      </Box>
    );
  }

  const weekOptions = parsedData.rows[0]?.weeks ?? [];
  const dayHeaders = parsedData.rows[0]?.weeks[activeWeek]?.days ?? [];

  const handleDelete = () => {
    if (deleteTarget) {
      dispatch(deleteRow(deleteTarget.id));
      setDeleteTarget(null);
    }
  };

  const handleSubmit = () => {
    dispatch(setUploadStatus("under_review"));
    navigate("/schedule-upload/approval");
  };

  const managerName =
    `${user?.osEmpFirstName ?? (user as any)?.os_emp_first_name ?? ""} ${user?.osEmpLastName ?? (user as any)?.os_emp_last_name ?? ""}`.trim() || "Manager";

  return (
    <Box sx={{ maxWidth: "100%", px: { xs: 2, md: 4 }, py: 4 }}>
      {/* ── Page header ── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "flex-start" }} spacing={2} mb={1}>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={800}>Review Schedule</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" mt={0.3}>
            <Typography variant="body2" color="text.secondary">
              {parsedData.fileName} &nbsp;·&nbsp; {parsedData.rows.length} employees
              &nbsp;·&nbsp; {parsedData.weekCount} week{parsedData.weekCount !== 1 ? "s" : ""}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            startIcon={<ArrowBack />}
            variant="outlined"
            color="inherit"
            onClick={() => navigate("/schedule-upload")}
            sx={{ borderRadius: 50 }}
          >
            Back
          </Button>
          <Button
            endIcon={<ArrowForward />}
            variant="contained"
            onClick={handleSubmit}
            sx={{ borderRadius: 50, px: 3, fontWeight: 700 }}
          >
            Submit for Approval
          </Button>
        </Stack>
      </Stack>

      <StepIndicator active={1} />

      {/* ── Legend ── */}
      <Box mt={2} mb={1.5}>
        <Legend />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, ml: 0.5 }}>
          ✓ Hover over any shift cell and click the X to clear it
        </Typography>
      </Box>

      {/* ── Week tabs + table ── */}
      <Paper
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, overflow: "hidden" }}
      >
        <Tabs
          value={activeWeek}
          onChange={(_, v) => setActiveWeek(v)}
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            background: "#f8fafc",
            "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          {weekOptions.map((w, i) => (
            <Tab
              key={i}
              label={w.weekLabel}
              sx={{ fontWeight: 600, fontSize: 13, textTransform: "none" }}
            />
          ))}
        </Tabs>

        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", overflowX: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Role */}
                <TableCell sx={{ minWidth: 110, background: "#f1f5f9", fontWeight: 700, fontSize: 12, color: "#475569", borderRight: "1px solid #e2e8f0" }}>
                  Role
                </TableCell>
                {/* Employee – sticky */}
                <TableCell sx={{ minWidth: 150, background: "#f1f5f9", fontWeight: 700, fontSize: 12, color: "#475569", borderRight: "1px solid #e2e8f0", position: "sticky", left: 0, zIndex: 3 }}>
                  Employee
                </TableCell>
                {/* Day columns */}
                {dayHeaders.map((d) => (
                  <TableCell
                    key={`${d.day}-${d.date}`}
                    align="center"
                    sx={{
                      background: ["Sat", "Sun"].includes(d.day) ? "#f8f4ff" : "#f1f5f9",
                      fontWeight: 700,
                      fontSize: 12,
                      color: ["Sat", "Sun"].includes(d.day) ? "#6a1b9a" : "#475569",
                      minWidth: 80,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box>{d.day}</Box>
                    <Box sx={{ fontWeight: 400, fontSize: 10, color: "text.secondary" }}>{d.date}</Box>
                  </TableCell>
                ))}
                {/* Delete */}
                <TableCell align="center" sx={{ background: "#f1f5f9", minWidth: 46, width: 46 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {parsedData.rows.map((row, ri) => {
                const week = row.weeks[activeWeek];
                const isEven = ri % 2 === 0;
                const roleColor = ROLE_COLORS[row.role] ?? "#64748b";

                return (
                  <TableRow
                    key={row.id}
                    sx={{ background: isEven ? "#fff" : "#f8fafc", "&:hover": { background: "#eef6ff" }, transition: "background 0.12s" }}
                  >
                    {/* Role */}
                    <TableCell sx={{ borderRight: "1px solid #e2e8f0", py: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: roleColor, background: `${roleColor}14`, px: 1, py: 0.3, borderRadius: 1, whiteSpace: "nowrap", fontSize: 10.5 }}
                      >
                        {row.role || "—"}
                      </Typography>
                    </TableCell>

                    {/* Employee – sticky */}
                    <TableCell sx={{ borderRight: "1px solid #e2e8f0", py: 1, position: "sticky", left: 0, background: isEven ? "#fff" : "#f8fafc", zIndex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{row.employee}</Typography>
                    </TableCell>

                    {/* Shift cells */}
                    {week?.days.map((d, di) => (
                      <TableCell
                        key={di}
                        align="center"
                        sx={{
                          py: 0.75,
                          px: 0.5,
                          background: ["Sat", "Sun"].includes(d.day)
                            ? (isEven ? "#faf5ff" : "#f5f0fc")
                            : undefined,
                        }}
                      >
                        <ShiftCell value={d.value} onClear={() => dispatch(clearShiftCell({ rowId: row.id, weekIndex: activeWeek, dayIndex: di }))} />
                      </TableCell>
                    ))}

                    {/* Delete */}
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Tooltip title="Remove from submission">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(row)}
                          sx={{ opacity: 0.55, "&:hover": { opacity: 1 } }}
                        >
                          <DeleteOutline sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Footer ── */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {parsedData.rows.length} employees &nbsp;·&nbsp; Reviewing as:
          </Typography>
          <Chip
            label={managerName}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Button
          endIcon={<ArrowForward />}
          variant="contained"
          size="large"
          onClick={handleSubmit}
          sx={{ borderRadius: 50, px: 4, fontWeight: 700 }}
        >
          Submit for Approval
        </Button>
      </Box>

      {/* ── Delete dialog ── */}
      <DeleteDialog
        employee={deleteTarget?.employee ?? ""}
        open={!!deleteTarget}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default ScheduleReviewPage;
